import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  DocumentData, 
  QueryDocumentSnapshot,
  onSnapshot
} from 'firebase/firestore';
import { db } from './firebase';

// Interface til bruger metadata (fra users collection)
// Denne type bør defineres mere specifikt baseret på din 'users' collection struktur
export type UserMetadata = DocumentData; 

// Interface til call data
export interface CallData {
  id: string;
  userId: string; // intern user_id (f.eks. P44)
  date: Date;
  duration: number;
  sentimentScore?: number;
  keyPhrases?: string[];
  transcription?: string;
  dominanceDistance?: number | null; // Primær dominansafstand (generelt beregnet)

  // Udvidede felter for AgentDashboard og andre komponenter
  voice_score?: number;
  tlr?: number;
  observed_values?: {
    dominance_mean?: number;
    dominance_distance?: number;
    pitch_std?: number;
    pitch_std_distance?: number;
    energy_std?: number;
    energy_std_distance?: number;
    speech_rate_mean?: number;
    tlr?: number;
    tlr_distance?: number;
    [key: string]: any;
  };
  action_punkt_1?: string;
  action_punkt_2?: string;
  action_punkt_3_observed_values?: string; 
  action_punkt_4_observed_values?: string;
  feedback_text?: string;
  segment_scores?: { intro?: number; middle?: number; end?: number };

  // Nyt felt specifikt for benchmark afstande fra rule_results
  benchmark_distances?: {
    dominance_distance?: number;
    pitch_std_distance?: number;
    energy_std_distance?: number;
    tlr_distance?: number;
  };

  // Nye felter til spørgsmål fra transcription_reactions
  transcription_reactions?: {
    hv_questions?: {
      examples: string[];
      total_count: number;
    };
    partial_accepts?: {
      examples: string[];
      total_count: number;
    };
    [key: string]: any; // For andre kategorier
  };
  
  // Inkluder rå data for fleksibilitet, hvis nødvendigt
  // rawData?: DocumentData; 
}

// Hent brugermetadata fra users collection
export const getUserMetadata = async (authUserId: string) => {
  try {
    const userRef = doc(db, 'users', authUserId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return userSnap.data();
    } else {
      return null;
    }
  } catch (error) {
    console.error('Fejl ved hentning af brugermetadata:', error);
    throw error;
  }
};

// Hent opkald for en agent baseret på Firebase Auth bruger-ID
export const getUserCalls = async (authUserId: string): Promise<CallData[]> => {
  try {
    // Hent brugermetadata
    const userData = await getUserMetadata(authUserId);
    
    if (!userData) {
      throw new Error('Brugeren findes ikke');
    }
    
    // Hvis brugeren er agent
    if (userData.role === 'agent') {
      // Brug user_id feltet til at filtrere data
      const callsRef = collection(db, userData.dataCollection);
      const q = query(callsRef, where('user_id', '==', userData.user_id));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          userId: data.user_id,
          date: data.date ? new Date(data.date) : new Date(),
          duration: data.duration || 0,
          sentimentScore: data.sentiment_score || 0,
          keyPhrases: data.key_phrases || [],
          transcription: data.transcription || '',
          dominanceDistance: data.dominance_distance || null,
          // Andre relevante felter
        };
      });
    } 
    // Hvis brugeren er manager
    else if (userData.role === 'management') {
      // Hent alle relevante data fra management collection
      const callsRef = collection(db, userData.dataCollection);
      const querySnapshot = await getDocs(callsRef);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          userId: data.user_id || '',
          date: data.date ? new Date(data.date) : new Date(),
          duration: data.duration || 0,
          sentimentScore: data.sentiment_score || 0,
          keyPhrases: data.key_phrases || [],
          transcription: data.transcription || '',
          dominanceDistance: data.dominance_distance || null,
          // Andre relevante felter
        };
      });
    }
    
    return [];
  } catch (error) {
    console.error('Fejl ved hentning af brugeropkald:', error);
    throw error;
  }
};

// Lyt til opkald i realtid
export const listenToUserCalls = (
  authUserId: string, 
  callback: (calls: CallData[], userContext: UserMetadata | null) => void
): (() => void) => { // Eksplicit returnering af unsubscribe funktion
  let unsubscribeSnapshot = () => {}; // Holder unsubscribe funktionen fra onSnapshot

  getUserMetadata(authUserId).then((userData) => {
    if (!userData) {
      console.error('Brugeren findes ikke i listenToUserCalls');
      callback([], null);
      return; 
    }
    
    console.error('DEBUG: Starter realtidslytning for bruger (listenToUserCalls):', userData);
    console.error('DEBUG: Bruger ID for filtrering (listenToUserCalls):', userData.user_id);
    console.error('DEBUG: Collection (listenToUserCalls):', userData.dataCollection);
    
    let q;
    
    if (userData.role === 'agent') {
      const callsRef = collection(db, userData.dataCollection);
      q = query(callsRef, where('user_id', '==', userData.user_id));
    } else { // For andre roller (f.eks. management), lyt til hele collection
      const callsRef = collection(db, userData.dataCollection);
      q = query(callsRef);
    }
    
    unsubscribeSnapshot = onSnapshot(q, (snapshot) => {
      console.error(`DEBUG: Modtog ${snapshot.docs.length} dokumenter fra Firestore i listenToUserCalls`);
      
      const calls = snapshot.docs.map(doc => {
        const data = doc.data();
        
        let dominanceValue = null; // For den generelle dominanceDistance
        if (data.dominance_distance !== undefined) dominanceValue = Number(data.dominance_distance);
        else if (data.dominanceDistance !== undefined) dominanceValue = Number(data.dominanceDistance);
        else if (data.dominans_afstand !== undefined) dominanceValue = Number(data.dominans_afstand);
        else {
          const dominanceKeys = Object.keys(data).filter(key => key.toLowerCase().includes('domin'));
          if (dominanceKeys.length > 0) dominanceValue = Number(data[dominanceKeys[0]]);
        }
        if (dominanceValue !== null && isNaN(dominanceValue)) dominanceValue = null;

        // Udpak benchmark afstande fra rule_results
        const benchmarkRuleDistances = data.rule_results?.benchmark_rule?.distances;
        const benchmark_distances = {
          dominance_distance: benchmarkRuleDistances?.dominance_distance !== undefined ? Number(benchmarkRuleDistances.dominance_distance) : undefined,
          pitch_std_distance: benchmarkRuleDistances?.pitch_std_distance !== undefined ? Number(benchmarkRuleDistances.pitch_std_distance) : undefined,
          energy_std_distance: benchmarkRuleDistances?.energy_std_distance !== undefined ? Number(benchmarkRuleDistances.energy_std_distance) : undefined,
          tlr_distance: benchmarkRuleDistances?.tlr_distance !== undefined ? Number(benchmarkRuleDistances.tlr_distance) : undefined,
        };

        const callEntry: CallData = {
          id: doc.id,
          userId: data.user_id || '',
          date: data.timestamp ? (typeof data.timestamp.toDate === 'function' ? data.timestamp.toDate() : new Date(data.timestamp)) : new Date(),
          duration: data.call_duration || data.duration || 0,
          sentimentScore: data.sentiment_score !== undefined ? Number(data.sentiment_score) : 0,
          keyPhrases: data.key_phrases || [],
          transcription: data.transcription || '',
          dominanceDistance: dominanceValue, // Den generelt beregnede

          voice_score: data.voice_score !== undefined ? Number(data.voice_score) : undefined,
          tlr: data.tlr !== undefined ? Number(data.tlr) : (data.observed_values?.tlr !== undefined ? Number(data.observed_values.tlr) : undefined),
          observed_values: data.observed_values,
          action_punkt_1: data.action_punkt_1,
          action_punkt_2: data.action_punkt_2,
          action_punkt_3_observed_values: data.action_punkt_3_observed_values,
          action_punkt_4_observed_values: data.action_punkt_4_observed_values,
          feedback_text: data.feedback_text,
          segment_scores: data.segment_scores,
          benchmark_distances: benchmark_distances, // Tilføj de udpakkede benchmark afstande
          transcription_reactions: data.transcription_reactions || undefined, // Tilføj spørgsmålsdata
        };
        return callEntry;
      });
      
      callback(calls, userData);
    }, (error) => {
      console.error('Fejl i onSnapshot i listenToUserCalls:', error);
      callback([], userData); 
    });
  }).catch(error => {
    console.error('Fejl ved opsætning af realtidslytter (getUserMetadata fejlede i listenToUserCalls):', error);
    callback([], null);
  });
  
  // Returner en funktion, der kalder den faktiske unsubscribe funktion
  return () => {
    unsubscribeSnapshot();
  };
};

// Hent detaljerede oplysninger om et specifikt opkald
export const getCallDetails = async (callId: string, collectionName: string): Promise<CallData | null> => {
  try {
    const callRef = doc(db, collectionName, callId);
    const callSnap = await getDoc(callRef);
    
    if (callSnap.exists()) {
      const data = callSnap.data();
      return {
        id: callSnap.id,
        userId: data.user_id || '',
        date: data.date ? new Date(data.date) : new Date(),
        duration: data.duration || 0,
        sentimentScore: data.sentiment_score || 0,
        keyPhrases: data.key_phrases || [],
        transcription: data.transcription || '',
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Fejl ved hentning af opkaldsdetaljer:', error);
    throw error;
  }
};

// Funktion til at hente statistikker for en agent
export const getAgentStatistics = async (authUserId: string) => {
  try {
    const calls = await getUserCalls(authUserId);
    
    if (!calls || calls.length === 0) {
      return {
        totalCalls: 0,
        averageDuration: 0,
        averageSentiment: 0,
        // Tilføj flere standardværdier efter behov
      };
    }
    
    const totalCalls = calls.length;
    const totalDuration = calls.reduce((sum, call) => sum + call.duration, 0);
    const totalSentiment = calls.reduce((sum, call) => sum + (call.sentimentScore || 0), 0); // Håndter undefined sentimentScore
    
    return {
      totalCalls,
      averageDuration: totalDuration / totalCalls,
      averageSentiment: totalSentiment / totalCalls,
      // Beregn og returner flere statistikker her
    };
  } catch (error) {
    console.error('Fejl ved hentning af agentstatistikker:', error);
    throw error;
  }
};

// Funktion til at hente alle agenter for management dashboardet
export const getAllAgents = async (authUserId: string) => {
  try {
    console.log("Forsøger at hente agenter med management bruger:", authUserId);
    
    // Verificér at brugeren er en manager
    const userData = await getUserMetadata(authUserId);
    
    if (!userData) {
      console.warn("Ingen brugerdata fundet for:", authUserId);
      return []; // Return empty array instead of throwing error
    }
    
    if (userData.role !== 'management') {
      console.warn("Bruger er ikke manager:", userData.role);
      return []; // Return empty array instead of throwing error
    }
    
    console.log("Management bruger bekræftet, henter agenter...");
    
    // Hent alle brugere med rollen "agent"
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('role', '==', 'agent'));
      const querySnapshot = await getDocs(q);
      
      console.log(`Fandt ${querySnapshot.docs.length} agenter i databasen`);
      
      // Returnér agent-data
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || 'Unnamed Agent',
          email: data.email || '',
          // Tilføj default værdier til fields som management dashboard forventer
          ...data
        };
      });
    } catch (queryError) {
      console.error("Fejl ved hentning af agent-liste:", queryError);
      return []; // Return empty array if query fails
    }
  } catch (error) {
    console.error('Fejl ved hentning af alle agenter:', error);
    return []; // Return empty array if any other error occurs
  }
}; 